import { useEffect, useRef, useState } from "react";

const STICKERS = ["★", "✦", "✧"];

interface Point {
  x: number;
  y: number;
  id: number;
  sticker: string;
  rotation: number;
}

/**
 * MouseTrail Component
 *
 * Creates a decorative trail of star stickers following the mouse cursor.
 * This component only renders on the client side.
 */
export function MouseTrail() {
  const [trail, setTrail] = useState<Point[]>([]);
  const [mounted, setMounted] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const counter = useRef(0);

  // Only render on client side
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle mouse movement
  useEffect(() => {
    if (!mounted) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;

      // Calculate distance to prevent too many stickers in one spot
      const dist = Math.hypot(
        clientX - lastPos.current.x,
        clientY - lastPos.current.y,
      );

      if (dist > 15) {
        lastPos.current = { x: clientX, y: clientY };
        counter.current += 1;

        const newPoint: Point = {
          x: clientX,
          y: clientY,
          id: Date.now() + Math.random(),
          sticker: STICKERS[counter.current % STICKERS.length],
          rotation: Math.random() * 360,
        };

        setTrail((prev) => [...prev.slice(-24), newPoint]);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mounted]);

  // Cleanup old items effect
  useEffect(() => {
    if (!mounted) return;

    const cleanup = setInterval(() => {
      setTrail((prev) => {
        if (prev.length === 0) return prev;
        return prev.slice(1);
      });
    }, 75);

    return () => clearInterval(cleanup);
  }, [mounted]);

  // Don't render on server side
  if (!mounted) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden">
      {trail.map((point, index) => {
        const progress = (index + 1) / trail.length;
        const scale = 0.5 + progress * 0.5;

        return (
          <div
            key={point.id}
            className="absolute leading-none text-[#111111] select-none"
            style={{
              left: point.x,
              top: point.y,
              fontSize: "24px",
              transform: `translate(-50%, -50%) rotate(${point.rotation}deg) scale(${scale})`,
              opacity: progress,
              transition: "transform 0.1s linear, opacity 0.1s linear",
            }}
          >
            {point.sticker}
          </div>
        );
      })}
    </div>
  );
}
